import { ReactFlowProvider, ReactFlow, Node, Edge } from "@xyflow/react";
import { useAtomValue } from "jotai";
import {
  nodeAtomFamily,
  ZapNodeData,
  zapStepNodesAtomFamily,
  allZapStepEdgesAtomFamily,
  suggestedNodeAtomFamily,
  suggestedZapStepsAndEdgesAtomFamily,
} from "@zapier/canvas-state";

import { PreviewProvider } from "../../../context/PreviewProvider";
import { ZapNode } from "../nodeTypes/ZapNode";
import { ZapStepNode } from "../nodeTypes/ZapStepNode";
import { ZapStepEdge } from "../edgeTypes/ZapStepEdge";

export function ZapPreview({ nodeId }: { nodeId: string }) {
  const node = useAtomValue(nodeAtomFamily(nodeId)) as Node<ZapNodeData>;
  const suggestedNode = useAtomValue(suggestedNodeAtomFamily(nodeId));
  const nodeOrSuggestedNode = node ?? suggestedNode;
  const zapSteps = useAtomValue(zapStepNodesAtomFamily(nodeId));
  const zapStepEdges = useAtomValue(allZapStepEdgesAtomFamily(nodeId));

  const suggestedZapStepsAndEdges = useAtomValue(
    suggestedZapStepsAndEdgesAtomFamily(nodeId),
  );

  if (nodeOrSuggestedNode.type !== "zap") {
    // TODO: implement preview for other types
    return null;
  }

  let nodes: Node[] = [];
  let edges: Edge[] = [];

  if (node) {
    /**
     * IMPORTANT: Preview uses the same node IDs as the real nodes
     *
     * This causes shared state between preview and real nodes, leading to bugs:
     * - `selected: false` - Without this override, selection state is shared with real node
     *   See: https://cdn.zappy.app/4f85a0440f688c8df276a3bc0e56ece8.gif
     * - `isExpanded: true` - Without this override, expand/collapse state is shared with real node
     *   See: https://cdn.zappy.app/40e74ed41a78c3b9c9870772554400a6.gif
     *
     * Why we use the same IDs despite these issues:
     * Atoms like nodeAtomFamily require matching IDs to display ghost node styling correctly and to hide preview asset button.
     */
    nodes = [
      {
        ...node,
        data: {
          ...node.data,
          isExpanded: true, // need this or else zap steps might not be expanded
        },
        selected: false, // need this or else the floating toolbar would show up
        selectable: false,
      },
      ...zapSteps.map((zStepNode) => ({
        ...zStepNode,
        hidden: false,
      })),
    ];

    edges = zapStepEdges;
  }

  if (!node && suggestedZapStepsAndEdges) {
    nodes = suggestedZapStepsAndEdges.nodes;
    edges = suggestedZapStepsAndEdges.edges;
  }

  return (
    <ReactFlowProvider>
      <PreviewProvider value={true}>
        <ReactFlow
          panOnDrag={true}
          panOnScroll={false}
          zoomOnScroll={true}
          zoomOnPinch={true}
          nodes={nodes}
          edges={edges}
          fitView
          nodeTypes={{
            zap: ZapNode,
            zapStep: ZapStepNode,
          }}
          edgeTypes={{
            zapStepEdge: ZapStepEdge,
          }}
          proOptions={{ hideAttribution: true }}
        />
      </PreviewProvider>
    </ReactFlowProvider>
  );
}
